import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Activity, Moon } from "lucide-react";

/** Format an integer second-count into a compact "1h 23m" / "45m" / "12s" string. */
export function fmtDuration(secs) {
  const s = Math.max(0, Math.round(Number(secs) || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return h > 0 ? `${h}h ${mm}m` : `${mm}m`;
}

export function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

const TONES = {
  sky: "from-sky-50 to-white dark:from-sky-900/30 dark:to-slate-800 border-sky-100 dark:border-sky-700/40 text-sky-700 dark:text-sky-200",
  emerald: "from-emerald-50 to-white dark:from-emerald-900/30 dark:to-slate-800 border-emerald-100 dark:border-emerald-700/40 text-emerald-700 dark:text-emerald-200",
  violet: "from-violet-50 to-white dark:from-violet-900/30 dark:to-slate-800 border-violet-100 dark:border-violet-700/40 text-violet-700 dark:text-violet-200",
  amber: "from-amber-50 to-white dark:from-amber-900/30 dark:to-slate-800 border-amber-100 dark:border-amber-700/40 text-amber-700 dark:text-amber-200",
};

export function StatTile({ icon: Icon, label, value, tone, testid }) {
  return (
    <div className={`rounded-2xl border bg-gradient-to-br ${TONES[tone] || TONES.sky} p-4`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
        <Icon className="h-4 w-4 opacity-70" />
      </div>
      <div className="mt-2 text-2xl font-bold" style={{ fontFamily: "Outfit" }}>{value}</div>
    </div>
  );
}

// Show the full 24-hour day. The grid scrolls horizontally on small screens.
const HEAT_HOURS = Array.from({ length: 24 }, (_, h) => h);
const fmtHour = (h) => {
  const ampm = h < 12 ? "a" : "p";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}${ampm}`;
};

function heatColor(v, max) {
  if (!v || v <= 0) return "bg-slate-50 dark:bg-slate-700/40 ring-1 ring-inset ring-slate-200/70 dark:ring-slate-600/50";
  const r = max > 0 ? v / max : 0;
  if (r > 0.75) return "bg-emerald-600 text-white";
  if (r > 0.5) return "bg-emerald-500 text-white";
  if (r > 0.3) return "bg-emerald-400 dark:text-slate-900";
  if (r > 0.15) return "bg-emerald-300 dark:text-slate-900";
  return "bg-emerald-200 dark:text-slate-900";
}

export function ActivityHeatmap({ hm, onCellClick, onLateNightClick }) {
  const { weekdays = [], minutes = [], sessions = [], max_minutes = 0, peak } = hm;
  // Late-night = 10pm–5am (hours 22,23,0–4). Sum across all weekdays.
  const LATE_HOURS = [22, 23, 0, 1, 2, 3, 4];
  let lateSessions = 0;
  let lateMinutes = 0;
  for (let wi = 0; wi < (sessions.length || 0); wi += 1) {
    for (const h of LATE_HOURS) {
      lateSessions += (sessions[wi] || [])[h] || 0;
      lateMinutes += (minutes[wi] || [])[h] || 0;
    }
  }
  lateMinutes = Math.round(lateMinutes * 10) / 10;
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4" data-testid="audit-heatmap">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
            <Activity className="h-4 w-4 text-emerald-500" /> When students study
          </div>
          <div className="text-xs text-slate-400 mt-0.5">Active minutes by weekday × hour · {hm.timezone || "local"} · click a slot to drill in</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {lateSessions > 0 && (
            <button
              type="button"
              onClick={onLateNightClick}
              className="text-xs px-2.5 py-1 rounded-full bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 font-medium inline-flex items-center gap-1 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 hover:ring-1 hover:ring-indigo-300 transition-colors cursor-pointer"
              title="Click to see every session that started between 10pm and 5am (Asia/Kolkata)"
              data-testid="audit-heatmap-late-night"
            >
              <Moon className="h-3.5 w-3.5" /> Late-night: {lateSessions} session{lateSessions === 1 ? "" : "s"} · {lateMinutes}m
            </button>
          )}
          {peak && (
            <div className="text-xs px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 font-medium" data-testid="audit-heatmap-peak">
              Peak: {weekdays[peak.weekday]} {fmtHour(peak.hour)} · {peak.minutes}m
            </div>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          {/* Hour header */}
          <div className="flex items-center gap-1 mb-1 pl-9">
            {HEAT_HOURS.map((h) => (
              <div key={h} className="w-6 text-center text-[9px] text-slate-400 font-mono">{fmtHour(h)}</div>
            ))}
          </div>
          {weekdays.map((wd, wi) => (
            <div key={wd} className="flex items-center gap-1 mb-1" data-testid={`heatmap-row-${wd}`}>
              <div className="w-8 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{wd}</div>
              {HEAT_HOURS.map((h) => {
                const m = (minutes[wi] || [])[h] || 0;
                const c = (sessions[wi] || [])[h] || 0;
                const active = m > 0 || c > 0;
                return (
                  <button
                    type="button"
                    key={h}
                    disabled={!active}
                    onClick={() => active && onCellClick?.(wi, h, wd)}
                    className={`w-6 h-6 rounded ${heatColor(m, max_minutes)} flex items-center justify-center text-[9px] font-semibold transition-transform ${active ? "hover:scale-110 cursor-pointer hover:ring-2 hover:ring-emerald-400" : "cursor-default"}`}
                    title={`${wd} ${fmtHour(h)} — ${m}m · ${c} session${c === 1 ? "" : "s"}${active ? " · click for details" : ""}`}
                    data-testid={`heatmap-cell-${wd}-${h}`}
                  >
                    {m > 0 && max_minutes > 0 && m / max_minutes > 0.5 ? Math.round(m) : ""}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 mt-3 text-[10px] text-slate-400">
        <span>Less</span>
        <div className="w-4 h-4 rounded bg-slate-50 dark:bg-slate-700/40 ring-1 ring-inset ring-slate-200/70 dark:ring-slate-600/50" />
        <div className="w-4 h-4 rounded bg-emerald-200" />
        <div className="w-4 h-4 rounded bg-emerald-300" />
        <div className="w-4 h-4 rounded bg-emerald-400" />
        <div className="w-4 h-4 rounded bg-emerald-500" />
        <div className="w-4 h-4 rounded bg-emerald-600" />
        <span>More</span>
      </div>
    </div>
  );
}

export function SimLogCard() {
  const [log, setLog] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/devadmin/sim-log"); setLog(data.log || []); } catch { /* noop */ }
    })();
  }, []);
  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
  const dur = (s) => {
    if (s == null) return "active";
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60), sec = s % 60;
    if (m < 60) return `${m}m ${sec}s`;
    return `${Math.floor(m / 60)}h ${m % 60}m`;
  };
  return (
    <div className="rounded-2xl border border-fuchsia-200 dark:border-fuchsia-900/50 bg-fuchsia-50/50 dark:bg-fuchsia-900/10 p-4" data-testid="sim-log-card">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-2 text-left">
        <span className="text-sm font-semibold text-fuchsia-700 dark:text-fuchsia-300 inline-flex items-center gap-2">
          <Activity className="h-4 w-4" /> Role Simulations ({log.length})
        </span>
        <span className="text-xs text-fuchsia-500">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="mt-3 space-y-1.5 max-h-64 overflow-y-auto" data-testid="sim-log-list">
          {log.length === 0 ? (
            <div className="text-xs text-slate-500">No simulations recorded yet.</div>
          ) : log.map((r, i) => (
            <div key={i} className="text-sm flex items-center justify-between gap-2 border-b border-fuchsia-100 dark:border-fuchsia-900/30 pb-1.5">
              <span className="text-slate-700 dark:text-slate-200">
                Viewed <span className="font-medium">{r.target_name}</span>
                <span className="text-slate-400"> ({r.target_type})</span>
              </span>
              <span className="text-xs text-slate-400 shrink-0 flex items-center gap-2">
                <span className={`px-1.5 py-0.5 rounded-full ${r.ended_at ? "bg-slate-100 dark:bg-slate-700" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"}`}>{dur(r.duration_s)}</span>
                {fmt(r.at)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
